/** Hook for managing Google Drive OAuth credentials via B2B Gateway App Credentials API.
 *
 * Uses Google Identity Services (GIS) authorization code model with popup UX.
 * Token exchange happens server-side via POST /reporting/gdrive-token-exchange.
 * See: https://developers.google.com/identity/oauth2/web/guides/use-code-model
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { getConfig } from "@/auth/config";
import { getUserManager } from "@/hooks/useAuth";
import type { AppCredential } from "@/types";

const CREDENTIAL_TYPE_ID = "google_drive_oauth";

/** All scopes required for full Google Services integration (REPORTING-SPEC-003). */
const GOOGLE_SCOPES = [
  "https://www.googleapis.com/auth/drive",
  "https://www.googleapis.com/auth/drive.labels",
  "https://www.googleapis.com/auth/spreadsheets",
  "https://www.googleapis.com/auth/documents",
].join(" ");

const GIS_SCRIPT_URL = "https://accounts.google.com/gsi/client";

export type ScopeStatus = "valid" | "needs_upgrade" | "disconnected";

async function gatewayFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const config = getConfig();
  const base = config?.b2bGatewayApiEndpoint;
  if (!base) {
    throw new Error(
      "Google Drive is not configured — b2bGatewayApiEndpoint is missing from config.json"
    );
  }
  const um = getUserManager();
  const user = await um?.getUser();
  const token = user?.id_token;

  if (!token) throw new Error("Not authenticated");

  const res = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? body.message ?? `${res.status}`);
  }
  return res.json();
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const config = getConfig();
  const base = config?.apiEndpoint ?? "";
  const um = getUserManager();
  const user = await um?.getUser();
  const token = user?.id_token;

  if (!token) throw new Error("Not authenticated");

  const res = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `${res.status}`);
  }
  return res.json();
}

/** Fetches Google OAuth Client ID and Picker API Key from platform settings. */
export function useGDriveConfig() {
  return useQuery({
    queryKey: ["gdrive-config"],
    queryFn: () => apiFetch<{ clientId: string; apiKey: string }>("/reporting/gdrive-config"),
    staleTime: 300_000,
  });
}

// GIS script loader
let gisLoaded = false;
let gisLoading = false;
const gisCallbacks: (() => void)[] = [];

function loadGisScript(): Promise<void> {
  if (gisLoaded || (typeof window !== "undefined" && window.google?.accounts?.oauth2)) {
    gisLoaded = true;
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    if (gisLoading) {
      gisCallbacks.push(resolve);
      return;
    }
    gisLoading = true;
    const script = document.createElement("script");
    script.src = GIS_SCRIPT_URL;
    script.onload = () => {
      gisLoaded = true;
      gisLoading = false;
      resolve();
      gisCallbacks.forEach((cb) => cb());
      gisCallbacks.length = 0;
    };
    script.onerror = () => {
      gisLoading = false;
      reject(new Error("Failed to load Google Identity Services"));
    };
    document.head.appendChild(script);
  });
}

/** Check if stored scopes contain all required scopes. */
function hasRequiredScopes(storedScopes: string | undefined): boolean {
  if (!storedScopes) return false;
  const stored = new Set(storedScopes.split(" "));
  return GOOGLE_SCOPES.split(" ").every((s) => stored.has(s));
}

interface CredentialWithScopes {
  credential: AppCredential | null;
  storedScopes: string | undefined;
}

export function useGDriveCredential() {
  const queryClient = useQueryClient();
  const { data: gdriveConfig } = useGDriveConfig();
  const connectingRef = useRef(false);

  const { data, isLoading } = useQuery<CredentialWithScopes>({
    queryKey: ["gdrive-credential"],
    queryFn: async () => {
      const resp = await gatewayFetch<{ credentials: AppCredential[] }>(
        `/app-credentials?credentialTypeId=${CREDENTIAL_TYPE_ID}`,
      );
      const cred = resp.credentials?.[0] ?? null;
      if (!cred || cred.status !== "active") return { credential: cred, storedScopes: undefined };

      // Fetch stored scopes from credential value
      try {
        const valueResp = await gatewayFetch<{ fields: { scopes?: string } }>(
          `/app-credentials/${cred.appCredentialId}/value`,
          { headers: { "X-Credential-Access": "retrieve" } },
        );
        return { credential: cred, storedScopes: valueResp.fields.scopes };
      } catch {
        return { credential: cred, storedScopes: undefined };
      }
    },
    staleTime: 60_000,
  });

  const credential = data?.credential ?? null;
  const isConnected = !!credential && credential.status === "active";

  const scopeStatus: ScopeStatus = !isConnected
    ? "disconnected"
    : hasRequiredScopes(data?.storedScopes)
      ? "valid"
      : "needs_upgrade";

  /** Initiate GIS consent flow — used for both initial connect and scope upgrade. */
  const initiateConsent = useCallback(async () => {
    const clientId = gdriveConfig?.clientId;
    if (!clientId) {
      console.error("Google Client ID not available from settings");
      return;
    }
    if (connectingRef.current) return;
    connectingRef.current = true;

    try {
      await loadGisScript();

      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: clientId,
        scope: GOOGLE_SCOPES,
        ux_mode: "popup",
        callback: async (response: { code?: string; error?: string }) => {
          connectingRef.current = false;
          if (response.error || !response.code) {
            console.error("Google OAuth error:", response.error);
            return;
          }

          try {
            const tokens = await apiFetch<{ access_token: string; refresh_token: string; expires_in: number }>(
              "/reporting/gdrive-token-exchange",
              {
                method: "POST",
                body: JSON.stringify({
                  code: response.code,
                  redirectUri: window.location.origin,
                }),
              },
            );

            // Store or update tokens in B2B Gateway
            if (credential) {
              // Update existing credential (scope upgrade)
              await updateGDriveTokens(credential.appCredentialId, tokens);
            } else {
              // Create new credential (first connect)
              await storeGDriveTokens(tokens);
            }

            queryClient.invalidateQueries({ queryKey: ["gdrive-credential"] });
          } catch (err) {
            console.error("Token exchange failed:", err);
          }
        },
      });

      client.requestCode();
    } catch (err) {
      connectingRef.current = false;
      console.error("Failed to initiate Google OAuth:", err);
    }
  }, [gdriveConfig, queryClient, credential]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!credential) return null;
    try {
      const resp = await gatewayFetch<{ fields: { accessToken: string } }>(
        `/app-credentials/${credential.appCredentialId}/value`,
        { headers: { "X-Credential-Access": "retrieve" } },
      );
      return resp.fields.accessToken;
    } catch {
      return null;
    }
  }, [credential]);

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!credential) return;
      await gatewayFetch(`/app-credentials/${credential.appCredentialId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gdrive-credential"] });
    },
  });

  return {
    credential,
    isLoading,
    isConnected,
    scopeStatus,
    connect: initiateConsent,
    reconnect: initiateConsent,
    getAccessToken,
    disconnect: disconnectMutation.mutate,
    googleApiKey: gdriveConfig?.apiKey ?? "",
    googleClientId: gdriveConfig?.clientId ?? "",
  };
}

/** Store new tokens in the B2B Gateway (initial connect). */
async function storeGDriveTokens(tokens: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}) {
  const expiry = new Date(Date.now() + tokens.expires_in * 1000).toISOString();
  await gatewayFetch("/app-credentials", {
    method: "POST",
    body: JSON.stringify({
      credentialTypeId: CREDENTIAL_TYPE_ID,
      label: "Google Drive",
      fields: {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        tokenExpiry: expiry,
        scopes: GOOGLE_SCOPES,
      },
    }),
  });
}

/** Update existing credential with new tokens (scope upgrade). */
async function updateGDriveTokens(
  appCredentialId: string,
  tokens: { access_token: string; refresh_token: string; expires_in: number },
) {
  const expiry = new Date(Date.now() + tokens.expires_in * 1000).toISOString();
  await gatewayFetch(`/app-credentials/${appCredentialId}`, {
    method: "PUT",
    body: JSON.stringify({
      fields: {
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        tokenExpiry: expiry,
        scopes: GOOGLE_SCOPES,
      },
    }),
  });
}
